import { useState } from 'react';
import { useIntegrations, useUpsertIntegration } from '../lib/queries.js';

type FieldSpec = string | { name: string; type: 'select'; options: string[] };

const fieldName = (f: FieldSpec): string => (typeof f === 'string' ? f : f.name);

const FIELDS: Record<string, { label: string; fields: FieldSpec[] }> = {
  openai: { label: 'OpenAI', fields: ['apiKey'] },
  google: { label: 'Google AI (Gemini)', fields: ['apiKey'] },
  google_oauth: { label: 'Google OAuth (Login)', fields: ['clientId', 'clientSecret'] },
  openrouter: { label: 'OpenRouter', fields: ['apiKey'] },
  deepgram: {
    label: 'Deepgram (transcrição de voz)',
    fields: ['apiKey', { name: 'model', type: 'select', options: ['nova-3', 'nova-2', 'flux'] }],
  },
  mercadopago: { label: 'Mercado Pago', fields: ['accessToken', 'publicKey', 'webhookSecret'] },
  resend: { label: 'Resend', fields: ['apiKey'] },
  sentry: { label: 'Sentry', fields: ['dsn'] },
  posthog: { label: 'PostHog', fields: ['apiKey', 'host'] },
};

export function IntegrationsPage() {
  const { data } = useIntegrations();
  const upsert = useUpsertIntegration();
  const [open, setOpen] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});

  async function save() {
    if (!open) return;
    const spec = FIELDS[open];
    if (!spec) return;
    const secret: Record<string, string> = {};
    for (const f of spec.fields) {
      const n = fieldName(f);
      if (values[n]) secret[n] = values[n]!;
    }
    await upsert.mutateAsync({ slug: open, label: spec.label, secret });
    setOpen(null);
    setValues({});
  }

  const configured = new Set((data?.integrations ?? []).filter((i) => i.configured).map((i) => i.slug));

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6">Integrações</h1>
      <p className="text-sm text-zinc-400 mb-4">
        Chaves cifradas AES-256-GCM no DB com sua <code>master.key</code>. Inputs nunca exibem valores salvos
        — apenas substituem.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.entries(FIELDS).map(([slug, spec]) => (
          <div key={slug} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4 flex items-center justify-between">
            <div>
              <div className="font-medium">{spec.label}</div>
              <div className="text-xs text-zinc-500 font-mono">{slug}</div>
            </div>
            <div className="flex items-center gap-3">
              {configured.has(slug) ? (
                <span className="text-xs text-emerald-400">✓ configurado</span>
              ) : (
                <span className="text-xs text-zinc-500">não configurado</span>
              )}
              <button
                onClick={() => {
                  setOpen(slug);
                  setValues({});
                }}
                className="text-xs rounded bg-zinc-800 px-3 py-1 hover:bg-zinc-700"
              >
                editar
              </button>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/60 grid place-items-center p-6" onClick={() => setOpen(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 max-w-md w-full"
          >
            <h2 className="text-lg font-semibold mb-1">{FIELDS[open]?.label}</h2>
            <p className="text-xs text-zinc-500 mb-3">Campos vazios mantêm valor anterior.</p>
            {FIELDS[open]?.fields.map((f) => {
              const n = fieldName(f);
              if (typeof f !== 'string' && f.type === 'select') {
                return (
                  <select
                    key={n}
                    value={values[n] ?? ''}
                    onChange={(e) => setValues({ ...values, [n]: e.target.value })}
                    className="w-full rounded bg-zinc-950 border border-zinc-800 px-3 py-2 mb-2 font-mono text-sm"
                  >
                    <option value="">{n} — (manter atual)</option>
                    {f.options.map((o) => (
                      <option key={o} value={o}>
                        {o === 'nova-3' ? 'nova-3 — pt-BR (recomendado)' : o}
                      </option>
                    ))}
                  </select>
                );
              }
              return (
                <input
                  key={n}
                  type="password"
                  placeholder={n}
                  value={values[n] ?? ''}
                  onChange={(e) => setValues({ ...values, [n]: e.target.value })}
                  className="w-full rounded bg-zinc-950 border border-zinc-800 px-3 py-2 mb-2 font-mono text-sm"
                />
              );
            })}
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setOpen(null)} className="text-sm text-zinc-400 px-3">cancelar</button>
              <button
                onClick={save}
                disabled={upsert.isPending}
                className="text-sm bg-rose-500 text-zinc-950 rounded px-3 py-1.5 disabled:opacity-50"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
